class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      // Older versions submitted catalog searches as `tag:term*`. Keep old
      // bookmarked/result URLs readable, but always show and resubmit the
      // customer's original term so Shopify can search every indexed field.
      this.input.value = this.getDisplaySearchTerm(this.input.value);
      this.input.form.addEventListener('submit', this.onSearchSubmit.bind(this));
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  buildSearchQuery(value) {
    return this.getDisplaySearchTerm(value).trim();
  }

  getDisplaySearchTerm(value) {
    const tokens = value.trim().split(/\s+/);
    const isCatalogQuery = tokens.every((token) => token.startsWith('tag:') && token.endsWith('*'));

    if (isCatalogQuery) {
      return tokens.map((token) => token.slice(4, -1)).join(' ');
    }

    return value.endsWith('*') ? value.slice(0, -1) : value;
  }

  normalizeCatalogTerm(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase(document.documentElement.lang || undefined);
  }

  getSearchIndex() {
    if (!window.routes?.search_index_url) return Promise.resolve([]);

    if (!SearchForm.searchIndexPromise) {
      SearchForm.searchIndexPromise = fetch(window.routes.search_index_url)
        .then((response) => {
          if (!response.ok) throw new Error(`Search index ${response.status}`);
          return response.json();
        })
        .then((data) => (Array.isArray(data.names) ? data.names : []))
        .catch(() => []);
    }

    return SearchForm.searchIndexPromise;
  }

  getEditDistance(source, target) {
    const previousRow = Array.from({ length: target.length + 1 }, (_, index) => index);

    for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex++) {
      const currentRow = [sourceIndex];

      for (let targetIndex = 1; targetIndex <= target.length; targetIndex++) {
        const substitutionCost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;
        currentRow[targetIndex] = Math.min(
          currentRow[targetIndex - 1] + 1,
          previousRow[targetIndex] + 1,
          previousRow[targetIndex - 1] + substitutionCost
        );
      }

      previousRow.splice(0, previousRow.length, ...currentRow);
    }

    return previousRow[target.length];
  }

  getCommonSuffixLength(source, target) {
    const maximumLength = Math.min(source.length, target.length);
    let suffixLength = 0;

    while (
      suffixLength < maximumLength &&
      source[source.length - suffixLength - 1] === target[target.length - suffixLength - 1]
    ) {
      suffixLength++;
    }

    return suffixLength;
  }

  async getCatalogSearchTerm(value) {
    const cleanValue = value.trim();
    const normalizedValue = this.normalizeCatalogTerm(cleanValue);
    const reservedTerms = new Set([
      'nino',
      'nina',
      'mujer',
      'hombre',
      'unisex',
      'kids',
      'bota',
      'botas',
      'botin',
      'botines',
      'zapato',
      'zapatos',
      'tenis',
      'sandalia',
      'sandalias',
      'mocasin',
      'mocasines',
      'balerina',
      'balerinas',
      'escolar',
      'casual',
      'piel',
      'sintetico',
      'negro',
      'cafe',
      'blanco',
      'rosa',
      'beige',
      'oferta',
    ]);

    if (
      normalizedValue.length < 4 ||
      normalizedValue.includes(' ') ||
      normalizedValue.includes('tag:') ||
      reservedTerms.has(normalizedValue) ||
      !/^[a-z]+$/.test(normalizedValue)
    ) {
      return cleanValue;
    }

    const names = await this.getSearchIndex();
    const exactMatch = names.find((name) => this.normalizeCatalogTerm(name) === normalizedValue);
    if (exactMatch) return exactMatch;

    const maximumDistance = normalizedValue.length <= 4 ? 1 : normalizedValue.length <= 8 ? 2 : 3;
    let closestName = cleanValue;
    let closestDistance = maximumDistance + 1;
    let closestLengthDifference = Number.POSITIVE_INFINITY;
    let closestSuffixLength = -1;
    let hasTie = false;

    names.forEach((name) => {
      const normalizedName = this.normalizeCatalogTerm(name);
      if (normalizedName.includes(' ')) return;

      const distance = this.getEditDistance(normalizedValue, normalizedName);
      const lengthDifference = Math.abs(normalizedValue.length - normalizedName.length);
      const suffixLength = this.getCommonSuffixLength(normalizedValue, normalizedName);
      const isCloser =
        distance < closestDistance ||
        (distance === closestDistance && lengthDifference < closestLengthDifference) ||
        (distance === closestDistance &&
          lengthDifference === closestLengthDifference &&
          suffixLength > closestSuffixLength);

      if (isCloser) {
        closestName = name;
        closestDistance = distance;
        closestLengthDifference = lengthDifference;
        closestSuffixLength = suffixLength;
        hasTie = false;
      } else if (
        distance === closestDistance &&
        lengthDifference === closestLengthDifference &&
        suffixLength === closestSuffixLength &&
        name !== closestName
      ) {
        hasTie = true;
      }
    });

    return closestDistance <= maximumDistance && !hasTie ? closestName : cleanValue;
  }

  async onSearchSubmit(event) {
    if (this.querySelector('[aria-selected="true"] a')) return;

    event.preventDefault();
    const catalogTerm = await this.getCatalogSearchTerm(this.input.value);
    this.input.value = this.buildSearchQuery(catalogTerm);
    this.input.form.submit();
  }

  shouldResetForm() {
    return true;
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
  }
}

customElements.define('search-form', SearchForm);
